import React, { useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { useEventApi } from "src/_common/hooks/actions/event/appEventApiHook";
import { useAppViewPicksModalApiResponseSelector } from "src/_common/hooks/selectors/eventSelector";

interface ViewPicksModalProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  rodeoEventId?: string;
  performanceId?: string;
  groupId?: string;
  onEditPicks?: () => void;
}

const ViewPicksModal: React.FC<ViewPicksModalProps> = ({
  isOpen,
  onClose,
  className = "",
  rodeoEventId,
  performanceId,
  groupId,
  onEditPicks,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  // const previousFocusRef = useRef<HTMLElement | null>(null);

  // API integration
  const { callGetRodeoEventYourPicks } = useEventApi();
  const MypicksResponse = useAppViewPicksModalApiResponseSelector();

  // Fetch your picks when modal opens and we have required params
  useEffect(() => {
    const params = {
      rodeo_event_id: rodeoEventId,
      performance_id: performanceId,
      group_id: groupId,
    };

    callGetRodeoEventYourPicks(
      params,
      (msg: string, resp: any) => {
        console.log("Your picks fetched successfully:", resp);
      },
      (err: string) => {
        console.error("Failed to fetch your picks:", err);
      }
    );
  }, []);

  const handleEditPicksClick = () => {
    // Close ViewPicks modal and open MakePicks modal
    onClose();
    if (onEditPicks) {
      // Small delay to ensure smooth modal transition
      setTimeout(() => {
        onEditPicks();
      }, 100);
    }
  };

  const handleRodeoStandingsClick = () => {
    // TODO: Implement rodeo standings functionality
    console.log("Rodeo standings clicked");
  };

  const handleShareClick = () => {
    // TODO: Implement share functionality
    console.log("Share clicked");
  };

  // Check if all events have winning_declare as 0 (meaning picks can still be edited)
  const canEditPicks = () => {
    if (!MypicksResponse?.team || MypicksResponse.team.length === 0) {
      return false;
    }
    return MypicksResponse.team.every((event: any) => event.winning_declare === 0);
  };

  if (!isOpen) return null;

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      className={`view-picks-modal ${className}`}
      centered
      size="lg"
    >
      <Modal.Body className="p-0">
        <div
          ref={modalRef}
          className="view-picks-modal-content"
          role="dialog"
          aria-modal="true"
          aria-label="View Picks Modal"
          tabIndex={-1}
        >
          {/* Close button */}
          <button className="btn-close-view-picks" onClick={onClose} aria-label="Close modal">
            ×
          </button>
          {/* Header */}
          <div className="view-picks-header">
            <div className="view-picks-brand-info">
              <div className="view-picks-logo">
                <img src={MypicksResponse?.performance?.avatar.thumb || "/img/avatar/avatar-1.png"} alt="Logo" />
              </div>
              <div className="view-picks-event-info">
                <h2 className="view-picks-title">{MypicksResponse?.group_dtls?.title}</h2>
                <p className="view-picks-title">{MypicksResponse?.rodeo_event_dtls?.title}</p>
                <p className="view-picks-location">
                  {MypicksResponse?.rodeo_event_dtls?.location} - {MypicksResponse?.performance?.title}
                </p>
              </div>
            </div>
            <button className="view-picks-share-btn" onClick={handleShareClick}>
              Share <i className="fas fa-share"></i>
            </button>
          </div>
          {/* Tie Breaker Code */}
          {MypicksResponse?.user_performance_tie_breaker &&
          MypicksResponse?.user_performance_tie_breaker?.tie_breaker_code !== 99999 ? (
            <div className="tie-breaker-section">
              <span className="tie-breaker-label">Tie Breaker Code: </span>
              <span className="tie-breaker-code">
                {MypicksResponse?.user_performance_tie_breaker?.tie_breaker_code}
              </span>
            </div>
          ) : null}
          {/* Events list */}
          <div className="view-picks-events-list">
            {MypicksResponse?.team && MypicksResponse.team.length > 0 ? (
              MypicksResponse.team.map((event: any, index: number) => (
                <div key={index} className={`view-picks-event-item`}>
                  <div className="event-content">
                    <span className="event-name">{event?.event_name}</span>
                    {event.name ? (
                      <div className="rider-info">
                        <img
                          src={event?.contestant_details?.contestant_dtls.avatar}
                          alt={event.name}
                          className="rider-avatar"
                        />
                        <span className="rider-name">{event.name}</span>
                      </div>
                    ) : (
                      <div className="rider-info">
                        <span className="not-picked">Not Picked</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-events-message">
                <p>No events available</p>
              </div>
            )}
          </div>
          {/* Action buttons */}
          <div className="view-picks-actions">
            {canEditPicks() && (
              <button className="btn-edit-picks" onClick={handleEditPicksClick}>
                Edit Picks <i className="fas fa-external-link-alt"></i>
              </button>
            )}
            <button className="btn-rodeo-standings" onClick={handleRodeoStandingsClick}>
              Rodeo Standings <i className="fas fa-trophy"></i>
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ViewPicksModal;
